import type { ReactNode } from 'react';

import { useMemo, createContext, useContext, useCallback, useState } from 'react';
import { useDebouncedEffect } from '~/hooks/useDebouncedEffect';
import { SORT_ORDER_LISTING_DEFAULT } from '../constants';
import { useToggle } from '~/hooks/useToggle';
import { useEffectRunOnce } from '~/hooks';

const SEARCH_DEBOUNCE_MS = 500;

export interface IOrderListingFilterContext {
  queryValue: string | undefined;
  deferredQueryValue: string | undefined;
  sortSelected: string[] | undefined;
  isShowSearch: boolean;
  isRefreshing: boolean;
  setIsRefreshing: (value: boolean) => void;
  handleShowSearch: () => void;
  handleHideSearch: () => void;
  handleCancelSearch: () => void;
  handleFiltersQueryChange: (value: string) => void;
  handleQueryValueRemove: () => void;
  handleSortChange: (value: string[]) => void;
}

export const OrderListingFilterContext = createContext<IOrderListingFilterContext | undefined>(
  undefined,
);
export interface IOrderListingFilterProvider {
  children: ReactNode;
}
export const OrderListingFilterProvider = ({ children }: IOrderListingFilterProvider) => {
  const [queryValue, setQueryValue] = useState<string | undefined>(undefined);
  const [sortSelected, setSortSelected] = useState<string[] | undefined>(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    value: isShowSearch,
    setTrue: handleShowSearch,
    setFalse: handleHideSearch,
  } = useToggle(false);

  const [deferredQueryValue, setDeferredQueryValue] = useState(queryValue);
  useDebouncedEffect(() => setDeferredQueryValue(queryValue), [queryValue], SEARCH_DEBOUNCE_MS);

  const handleFiltersQueryChange = useCallback((value: string) => setQueryValue(value), []);

  const handleQueryValueRemove = useCallback(() => setQueryValue(undefined), []);

  const handleCancelSearch = useCallback(() => {
    handleHideSearch();
    handleQueryValueRemove();
  }, [handleHideSearch, handleQueryValueRemove]);

  const handleSortChange = useCallback(
    (value: string[]) => {
      setSortSelected(value);
    },
    [setSortSelected],
  );

  useEffectRunOnce(() => {
    handleSortChange(SORT_ORDER_LISTING_DEFAULT);
  }, true);

  const contextValue = useMemo<IOrderListingFilterContext>(
    () => ({
      queryValue,
      deferredQueryValue,
      sortSelected,
      isShowSearch,
      isRefreshing,
      setIsRefreshing,
      handleShowSearch,
      handleHideSearch,
      handleCancelSearch,
      handleFiltersQueryChange,
      handleQueryValueRemove,
      handleSortChange,
    }),
    [
      queryValue,
      deferredQueryValue,
      sortSelected,
      isShowSearch,
      isRefreshing,
      setIsRefreshing,
      handleShowSearch,
      handleHideSearch,
      handleCancelSearch,
      handleFiltersQueryChange,
      handleQueryValueRemove,
      handleSortChange,
    ],
  );

  return (
    <OrderListingFilterContext.Provider value={contextValue}>
      {children}
    </OrderListingFilterContext.Provider>
  );
};

export const useOrderListingFilterContext = () => {
  const context = useContext(OrderListingFilterContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
